import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const STAGES = ["target", "contacted", "negotiating", "confirmed", "recorded", "published"];

export default function Guests() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({
    name: "", org: "", category: "",
    mission_alignment_score: 5, credibility_score: 5, audience_score: 5,
    stage: "target", next_action: "", contact_info: "",
  });

  const load = async () => setItems((await api.get("/guests")).data);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/guests", f);
    toast.success("Guest added");
    setShowForm(false);
    setF({ ...f, name: "", org: "", category: "", next_action: "" });
    load();
  };

  const move = async (id, stage) => {
    await api.patch(`/guests/${id}`, { stage });
    load();
  };

  const grouped = STAGES.map((s) => ({ stage: s, items: items.filter((g) => g.stage === s) }));

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <PageHeader
        overline="CRM · Talent Pipeline"
        title="Guests"
        subtitle="Mission alignment × credibility × audience. Score everyone."
        actions={<button data-testid="new-guest-btn" onClick={() => setShowForm(true)} className="btn-primary text-sm flex items-center gap-1"><Plus size={14} /> New guest</button>}
      />

      {showForm && (
        <div className="oz-card p-5 mb-6">
          <form onSubmit={create} className="grid md:grid-cols-2 gap-3">
            <input required placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Organization" value={f.org} onChange={(e) => setF({ ...f, org: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <select value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} className="bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <div className="col-span-2 grid grid-cols-3 gap-3">
              <label className="text-xs text-[color:var(--oz-text-dim)]">Mission {f.mission_alignment_score}
                <input type="range" min="1" max="10" value={f.mission_alignment_score} onChange={(e) => setF({ ...f, mission_alignment_score: +e.target.value })} className="w-full" />
              </label>
              <label className="text-xs text-[color:var(--oz-text-dim)]">Credibility {f.credibility_score}
                <input type="range" min="1" max="10" value={f.credibility_score} onChange={(e) => setF({ ...f, credibility_score: +e.target.value })} className="w-full" />
              </label>
              <label className="text-xs text-[color:var(--oz-text-dim)]">Audience {f.audience_score}
                <input type="range" min="1" max="10" value={f.audience_score} onChange={(e) => setF({ ...f, audience_score: +e.target.value })} className="w-full" />
              </label>
            </div>
            <input placeholder="Next action" value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} className="col-span-2 bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] px-3 py-2 text-sm rounded-sm" />
            <div className="col-span-2 flex gap-2">
              <button className="btn-primary text-sm">Save</button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-sm">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {grouped.map((col) => (
          <div key={col.stage} className="kanban-col p-3" data-testid={`guest-col-${col.stage}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="overline text-[10px]">{col.stage}</div>
              <div className="text-xs font-mono text-[color:var(--oz-text-muted)]">{col.items.length}</div>
            </div>
            <div className="space-y-2">
              {col.items.map((g) => {
                const avg = ((g.mission_alignment_score + g.credibility_score + g.audience_score) / 3).toFixed(1);
                return (
                  <div key={g.id} className="p-3 rounded-sm border border-[color:var(--oz-border)] bg-[color:var(--oz-page)] hover:border-[color:var(--oz-emerald)] transition-colors" data-testid={`guest-${g.id}`}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-sm">{g.name}</div>
                      <span className="chip chip-gold font-mono text-[10px]">{avg}</span>
                    </div>
                    <div className="text-[11px] text-[color:var(--oz-text-muted)] mb-2">{g.org} · {g.category}</div>
                    <div className="text-[10px] font-mono text-[color:var(--oz-text-dim)] mb-2">
                      M{g.mission_alignment_score} · C{g.credibility_score} · A{g.audience_score}
                    </div>
                    {g.next_action && <div className="text-[11px] text-[color:var(--oz-text-dim)] mb-2 italic">→ {g.next_action}</div>}
                    <select value={g.stage} onChange={(e) => move(g.id, e.target.value)} className="w-full bg-transparent border border-[color:var(--oz-border)] px-2 py-1 text-[10px] rounded-sm font-mono">
                      {STAGES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
